import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldCheck, RefreshCw, Search, CheckCircle2, XCircle, AlertTriangle,
  User as UserIcon,
} from "lucide-react";
import { ADMIN_MODULES, MODULE_BY_ROUTE, moduleForAdminPath } from "@/lib/admin-modules";
import { toast } from "sonner";

export default function PermissionsDiagnosticPage() {
  const {
    user, axiosAuth, permissions, isSuperAdmin, hasPermission, refreshPermissions,
  } = useAuth();
  const [selfData, setSelfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [userDiagnostic, setUserDiagnostic] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);

  const fetchSelf = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/admin/diagnostics/permissions");
      setSelfData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando diagnóstico");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await axiosAuth().get("/users");
      setAllUsers(res.data?.items || res.data || []);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => { fetchSelf(); fetchAllUsers(); }, []);  // eslint-disable-line

  const inspectUser = async (uid) => {
    if (!isSuperAdmin) return;
    setLoadingUser(true);
    setUserDiagnostic(null);
    try {
      const res = await axiosAuth().get(`/admin/diagnostics/permissions/${uid}`);
      setUserDiagnostic(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error consultando usuario");
    } finally {
      setLoadingUser(false);
    }
  };

  const refreshMyPerms = async () => {
    await refreshPermissions();
    await fetchSelf();
    toast.success("Permisos refrescados");
  };

  // Computar rutas permitidas/bloqueadas para el usuario actual
  const allRoutes = Object.entries(MODULE_BY_ROUTE).sort();
  const allowedRoutes = allRoutes.filter(([, m]) => isSuperAdmin || hasPermission(m, "ver"));
  const blockedRoutes = allRoutes.filter(([, m]) => !(isSuperAdmin || hasPermission(m, "ver")));

  return (
    <div className="space-y-6" data-testid="permissions-diagnostic-page">
      <div>
        <h1 className="heading-industrial text-3xl uppercase font-bold text-slate-900">
          Diagnóstico de Permisos
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Vista en tiempo real del cálculo de permisos para tu usuario y, si eres super_admin, para cualquier usuario.
        </p>
      </div>

      {/* Tu sesión */}
      <Card className="border-slate-200 rounded-sm" data-testid="diag-self-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-red-600" /> Tu sesión
          </CardTitle>
          <Button onClick={refreshMyPerms} disabled={loading} size="sm" variant="ghost"
            className="h-7 px-2 text-[10px] uppercase tracking-wider"
            data-testid="refresh-self-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refrescar permisos
          </Button>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Stat label="Usuario" value={user?.email || user?.name || "—"} />
            <Stat label="Role slug" value={selfData?.user?.role || user?.role || "—"} />
            <Stat label="Es super_admin" value={isSuperAdmin ? "✓ SÍ" : "—"} color={isSuperAdmin ? "g" : "default"} />
          </div>
          {selfData?.role_doc && (
            <div className="text-[11px] text-slate-600">
              Rol en BD: <code className="bg-slate-100 px-1 rounded">{selfData.role_doc.name}</code>
              <span className="ml-2">level={selfData.role_doc.level}</span>
              <span className="ml-2">active={String(selfData.role_doc.active)}</span>
            </div>
          )}

          {/* Matriz de permisos */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Permisos efectivos por módulo</p>
            {isSuperAdmin ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm text-green-900 text-xs">
                <ShieldCheck className="inline w-4 h-4 mr-1" />
                super_admin · acceso total a todos los módulos y acciones
              </div>
            ) : (
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-1.5 px-2 font-semibold">Módulo</th>
                      <th className="text-left py-1.5 px-2 font-semibold">Acciones</th>
                      <th className="text-left py-1.5 px-2 font-semibold">VER?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(ADMIN_MODULES).map(([slug, meta]) => {
                      const acts = permissions?.[slug] || [];
                      const canVer = acts.includes("ver") || acts.includes("todo");
                      return (
                        <tr key={slug} className="border-t border-slate-100" data-testid={`diag-row-${slug}`}>
                          <td className="py-1 px-2 font-mono text-[10px]">{slug}<br/><span className="text-slate-400">{meta.label}</span></td>
                          <td className="py-1 px-2">{acts.length ? acts.map(a => <Badge key={a} variant="outline" className="text-[9px] mr-1">{a}</Badge>) : <span className="text-slate-400">—</span>}</td>
                          <td className="py-1 px-2">
                            {canVer
                              ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 inline" />
                              : <XCircle className="w-3.5 h-3.5 text-red-500 inline" />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Rutas permitidas vs bloqueadas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-green-200 rounded-sm p-2 bg-green-50/50" data-testid="diag-allowed-routes">
              <p className="text-[10px] uppercase tracking-wider text-green-900 font-bold mb-1">
                Rutas permitidas ({allowedRoutes.length})
              </p>
              <ul className="text-[10px] space-y-0.5 max-h-48 overflow-auto">
                {allowedRoutes.map(([p, m]) => (
                  <li key={p} className="flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                    <code className="font-mono">/admin/{p || "(dashboard)"}</code>
                    <span className="text-slate-400">→ {m}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-red-200 rounded-sm p-2 bg-red-50/50" data-testid="diag-blocked-routes">
              <p className="text-[10px] uppercase tracking-wider text-red-900 font-bold mb-1">
                Rutas bloqueadas ({blockedRoutes.length})
              </p>
              <ul className="text-[10px] space-y-0.5 max-h-48 overflow-auto">
                {blockedRoutes.map(([p, m]) => (
                  <li key={p} className="flex items-center gap-1">
                    <XCircle className="w-3 h-3 text-red-600 flex-shrink-0" />
                    <code className="font-mono">/admin/{p || "(dashboard)"}</code>
                    <span className="text-slate-400">→ {m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inspeccionar a otro usuario (super_admin) */}
      {isSuperAdmin && (
        <Card className="border-slate-200 rounded-sm" data-testid="diag-inspect-card">
          <CardHeader className="pb-3">
            <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
              <Search className="w-4 h-4 text-red-600" /> Inspeccionar a otro usuario
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input value={searchEmail} onChange={e => setSearchEmail(e.target.value)}
                placeholder="Filtrar por email o nombre..."
                className="rounded-sm h-9 border-slate-200 flex-1"
                data-testid="diag-search-input" />
            </div>
            <div className="border border-slate-200 rounded-sm max-h-72 overflow-auto" data-testid="diag-users-list">
              {allUsers
                .filter(u => {
                  if (!searchEmail) return true;
                  const q = searchEmail.toLowerCase();
                  return (u.email || "").toLowerCase().includes(q) ||
                    (u.name || "").toLowerCase().includes(q);
                })
                .slice(0, 30)
                .map(u => (
                  <div
                    key={u.id}
                    className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-xs"
                    onClick={() => inspectUser(u.id)}
                    data-testid={`diag-user-row-${u.email}`}
                  >
                    <span className="flex-1 truncate">{u.name || u.email}</span>
                    <code className="text-[10px] text-slate-400">{u.role}</code>
                  </div>
                ))}
            </div>

            {userDiagnostic && (
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/40" data-testid="diag-user-result">
                <p className="font-bold text-xs mb-2">
                  {userDiagnostic.user.name || userDiagnostic.user.email} <span className="text-slate-400">({userDiagnostic.user.role})</span>
                </p>
                {userDiagnostic.is_super_admin ? (
                  <p className="text-green-800 text-xs"><ShieldCheck className="inline w-4 h-4 mr-1" /> super_admin (acceso total)</p>
                ) : (
                  <div>
                    {Object.keys(userDiagnostic.permissions || {}).length === 0 ? (
                      <p className="text-amber-900 text-xs flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Sin permisos (matriz vacía o rol inexistente)
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                        {Object.entries(userDiagnostic.permissions).map(([m, acts]) => (
                          <div key={m}>
                            <code className="text-[10px]">{m}</code>: <span className="text-slate-600">{Array.isArray(acts) ? acts.join(", ") : String(acts)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {loadingUser && <p className="text-[10px] text-slate-400 mt-1">Cargando...</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, color = "default" }) {
  const colorClass = color === "g" ? "text-green-700" : "text-slate-900";
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-sm font-mono font-bold ${colorClass}`}>{value}</p>
    </div>
  );
}
